import { action, computed, observable } from 'mobx'
import { pattern, required, validationContext } from 'validx'
import { isValidUsPhoneNumber, validateObject } from '@/utils/validx-util'
import type { ContactInfo } from './ContactInfo'
import { PhysicalAddressEditorViewModel } from './PhysicalAddressEditorViewModel'

/**
 * View model for editing contact information.
 */
export class ContactInfoEditorViewModel {
  @observable private accessor _email = ''
  @observable private accessor _phoneNumber = ''

  readonly physicalAddressVm = new PhysicalAddressEditorViewModel()

  readonly validation = validationContext(this, {
    email: [
      required('Email is required'),
      pattern('email', 'Must be a valid email'),
    ],
    phoneNumber: [required('Phone number is required'), isValidUsPhoneNumber()],
    physicalAddressVm: [validateObject()],
  })

  @computed get email() {
    return this._email
  }

  set email(value: string) {
    this._email = value
    this.validation.clearErrors('email')
  }

  @computed get phoneNumber() {
    return this._phoneNumber
  }

  set phoneNumber(value: string) {
    this._phoneNumber = value
    this.validation.clearErrors('phoneNumber')
  }

  /**
   * Resets the state of the view model to that of the given info, or
   * defaults if none is specified.
   */
  @action.bound
  reset(info?: ContactInfo) {
    this.email = info?.email ?? ''
    this.phoneNumber = info?.phoneNumber ?? ''
    this.physicalAddressVm.reset(info?.physicalAddress)
    this.validation.reset()
  }

  /**
   * Maps the state of the VM to {@link ContactInfo}.
   */
  toContactInfo(): ContactInfo {
    return {
      email: this.email,
      phoneNumber: this.phoneNumber,
      physicalAddress: this.physicalAddressVm.toPhysicalAddress(),
    }
  }
}
